import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Line, LineChart, ReferenceLine, Tooltip, YAxis } from "recharts";
import { api, errMsg, num, rupee, pnlClass } from "@/lib/api";

const SparkTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return <div className="rounded border border-slate-700 bg-[#0D121F] px-2 py-1 text-[10px] text-slate-200">Week of {p.week}: <b className={pnlClass(p.pnl)}>{rupee(p.pnl)}</b> · {p.trades}t</div>;
};

const Spark = ({ pts, id }) => {
  if (!pts?.some((p) => p.trades)) return <span className="text-[10px] text-slate-600">—</span>;
  const last = pts[pts.length - 1].pnl, total = pts.reduce((a, p) => a + p.pnl, 0);
  return (
    <div className="flex items-center gap-2" data-testid={id}>
      <LineChart width={120} height={28} data={pts} margin={{ top: 3, bottom: 3, left: 2, right: 2 }}>
        <YAxis hide domain={["dataMin", "dataMax"]} />
        <ReferenceLine y={0} stroke="#334155" />
        <Tooltip content={<SparkTip />} cursor={{ stroke: "#475569" }} wrapperStyle={{ zIndex: 50 }} />
        <Line type="monotone" dataKey="pnl" stroke={total >= 0 ? "#34d399" : "#f87171"} strokeWidth={1.5} dot={{ r: 1.5 }} isAnimationActive={false} />
      </LineChart>
      <span className={`text-[10px] ${pnlClass(total)}`} title={`8-week total · last week ${rupee(last)}`}>{rupee(total)}</span>
    </div>
  );
};

const Cell = ({ s, id }) => (
  <td data-testid={id} className={s.trades ? pnlClass(s.pnl) : "text-slate-600"}>
    {s.trades ? <>{rupee(s.pnl)} <span className="text-[10px] text-slate-500">· {s.trades}t · PF {s.pf == null ? "∞" : num(s.pf)}</span></> : "—"}
  </td>
);

export const WeeklyScorecard = ({ onChange }) => {
  const [week, setWeek] = useState(null);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState(null);
  useEffect(() => { api.get("/execution/scorecard/history").then((r) => setHist(r.data)).catch(() => {}); }, []);
  const load = useCallback(() => api.get("/execution/scorecard", { params: week ? { week } : {} }).then((r) => setD(r.data)).catch(() => {}), [week]);
  useEffect(() => { load(); }, [load]);
  const off = async (kind) => {
    setBusy(true);
    try { await api.post("/execution/strategies", { kind, enabled: false }); toast.success("Strategy switched off"); load(); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  const active = d.rows.filter((r) => r.combined.trades || hist?.series?.[r.kind]?.some((p) => p.trades));
  const losers = d.rows.filter((r) => r.loser);
  return (
    <div className="panel col-span-12" data-testid="panel-weekly-scorecard">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 px-4 py-2.5">
        <span className="eyebrow">Weekly strategy scorecard · live + dry run</span>
        <select data-testid="select-scorecard-week" value={d.week} onChange={(e) => setWeek(e.target.value)}
          className="h-7 rounded border border-slate-700 bg-slate-900 px-2 text-[11px] text-slate-200 focus:border-indigo-500 focus:outline-none">
          {d.weeks.map((w, i) => <option key={w} value={w}>{i === 0 ? `This week (from ${w})` : `Week of ${w}`}</option>)}
        </select>
      </div>
      <p className="px-4 pt-2 text-[11px] text-slate-500">
        Ranked by combined P&amp;L for {d.week} → {d.week_end}. Flagged when the week loses money over 3+ trades, or the profit factor is below 1. Nothing is switched off unless you tap the button.
        {losers.length > 0 && <span className="text-amber-300/90" data-testid="scorecard-loser-count"> {losers.length} to review.</span>}
      </p>
      <div className="overflow-x-auto">
        <table className="dense num w-full" data-testid="table-weekly-scorecard">
          <thead><tr><th>#</th><th className="!text-left">Strategy</th><th>Live</th><th>Dry run</th><th>Combined</th><th>Win rate</th><th className="!text-left">8-week trend</th><th className="!text-left">Suggestion</th></tr></thead>
          <tbody>
            {active.length === 0 && <tr><td colSpan={8} className="!py-6 !text-center text-slate-500" data-testid="scorecard-empty">No live or dry-run trades in the last 8 weeks.</td></tr>}
            {active.map((r) => (
              <tr key={r.kind} data-testid={`scorecard-row-${r.kind}`} className={r.loser ? "bg-red-500/[0.04]" : ""}>
                <td className="text-slate-500">{r.rank}</td>
                <td className="!text-left text-slate-200">{r.label}{r.enabled && <span className="ml-1 text-[10px] text-emerald-400">ON</span>}</td>
                <Cell s={r.live} id={`scorecard-live-${r.kind}`} /><Cell s={r.dry} id={`scorecard-dry-${r.kind}`} /><Cell s={r.combined} id={`scorecard-combined-${r.kind}`} />
                <td>{r.combined.trades ? `${num((r.combined.wins / r.combined.trades) * 100, 0)}%` : "—"}</td>
                <td className="!text-left"><Spark pts={hist?.series?.[r.kind]} id={`scorecard-spark-${r.kind}`} /></td>
                <td className="!text-left">
                  {r.loser ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] text-red-300" data-testid={`scorecard-reason-${r.kind}`}>{r.reason}</span>
                      {r.enabled ? (
                        <button data-testid={`button-scorecard-off-${r.kind}`} disabled={busy} onClick={() => off(r.kind)}
                          className="rounded border border-red-500/40 px-2 py-0.5 text-[11px] font-semibold text-red-300 transition-colors hover:bg-red-500/10 disabled:opacity-50">Switch off</button>
                      ) : <span className="text-[10px] text-slate-500">already off</span>}
                    </div>
                  ) : <span className="text-[11px] text-emerald-400/80">Keep</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
